/** 数据表：粘性表头、横向滚动、游标分页。 */

import type { ReactNode } from 'react';
import { Button, Empty, ErrorBox, Loading } from './ui.tsx';

export interface Column<T> {
  readonly key: string;
  readonly header: ReactNode;
  /** 数字列：右对齐 + 等宽数字。金额、计数、比率都该置 true。 */
  readonly numeric?: boolean;
  readonly render: (row: T) => ReactNode;
}

export function DataTable<T>({
  columns, rows, loading, error, empty, emptyHint, onRowClick, rowKey,
}: {
  columns: readonly Column<T>[];
  rows: readonly T[];
  loading?: boolean;
  error?: unknown;
  empty?: string;
  emptyHint?: ReactNode;
  onRowClick?: (row: T) => void;
  rowKey: (row: T) => string;
}) {
  if (error !== null && error !== undefined) {
    return <div className="p-3.5"><ErrorBox error={error} /></div>;
  }
  if (loading === true && rows.length === 0) return <Loading />;
  if (rows.length === 0) {
    return <Empty title={empty ?? '暂无数据'} {...(emptyHint === undefined ? {} : { hint: emptyHint })} />;
  }

  return (
    <div className="table-scroll">
      <table className="data">
        <thead>
          <tr>
            {columns.map((column) => (
              <th key={column.key} className={column.numeric === true ? 'num' : undefined}>
                {column.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr
              key={rowKey(row)}
              {...(onRowClick === undefined
                ? {}
                : { 'data-clickable': 'true', onClick: () => { onRowClick(row); } })}
            >
              {columns.map((column) => (
                <td key={column.key} className={column.numeric === true ? 'num' : undefined}>
                  {column.render(row)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * 游标分页控件。
 *
 * 不显示总数 —— 后端用的是游标分页，压根没有 count(*)。
 * 编一个「共 N 条」出来只会误导人，而真去 count 一次会在大表上拖垮列表接口。
 */
export function Pager({
  page, hasNext, hasPrev, onNext, onPrev, loading, count,
}: {
  page: number;
  hasNext: boolean;
  hasPrev: boolean;
  onNext: () => void;
  onPrev: () => void;
  loading?: boolean;
  count: number;
}) {
  if (!hasNext && !hasPrev) {
    return count === 0 ? null : (
      <div className="pager"><span>共 {count} 条</span></div>
    );
  }
  return (
    <div className="pager">
      <span>第 {page + 1} 页 · 本页 {count} 条</span>
      <span className="flex-1" />
      <Button size="sm" onClick={onPrev} disabled={!hasPrev || loading === true}>上一页</Button>
      <Button size="sm" onClick={onNext} disabled={!hasNext || loading === true}>下一页</Button>
    </div>
  );
}
